import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Modal,
  Button,
  Badge,
  Spinner,
} from 'react-bootstrap';
import articleService from '../../services/articleService';
import { Article } from '../../models/article';
import './Common.scss';

const Welcome: React.FC = () => {
  // ステート管理
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [showModal, setShowModal] = useState(false);

  // 記事データを取得
  useEffect(() => {
    const loadArticles = async () => {
      setLoading(true);
      try {
        const response = await articleService.getArticles({
          limit: 5, // 最新5件を取得
          sortBy: 'created_at',
          sortOrder: 'desc',
        });
        setArticles(response.articles);
      } catch (error) {
        console.error('記事の取得に失敗しました:', error);
      } finally {
        setLoading(false);
      }
    };

    loadArticles();
  }, []);

  // 記事をクリックした時の処理
  const handleArticleClick = (article: Article) => {
    setSelectedArticle(article);
    setShowModal(true);
  };

  // モーダルを閉じる処理
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedArticle(null);
  };

  // 日付をフォーマットする関数
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP');
  };

  // HTMLタグを除去してプレーンテキストに変換
  const stripHtml = (html: string) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // シンプルな画像URL構築（フック使用を最小限に）
  const getImageUrl = (photoPath: string | null): string | null => {
    if (!photoPath) return null;

    if (photoPath.startsWith('http://') || photoPath.startsWith('https://')) {
      return photoPath;
    }

    const baseUrl =
      process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    const cleanPath = photoPath.replace(/^\/+/, '');
    return `${baseUrl}/${cleanPath}`;
  };

  return (
    <Container fluid>
      <Row className="justify-content-center">
        <Col lg={12} md={12} sm={12} xs={12} className="mt-4">
          <Card className="shadow-sm slideinBottom">
            <Card.Body className="text-center py-5">
              <h1 className="display-4 mb-4 text-success">ようこそ</h1>
              <h2 className="h3 text-muted mb-4">
                ヒーリングサロンGundariのページをお開きいただきありがとうございます！
              </h2>
              <h2 className="h3 text-muted mb-4">こちらはメインページです。</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={8} md={8} sm={12} xs={12} className="mt-4 d-flex">
          <Card className="shadow-sm h-100 slideinBottom">
            <Card.Body className="text-center py-5 d-flex flex-column justify-content-center">
              <h1 className="display-4 mb-4 text-success">サロン紹介</h1>
              <h2 className="h3 text-danger mb-4">
                まだ固定のサロンはありません。対面のセッションは出張で対応しています。出張対応可能なエリアは岐阜県西濃地域です。
              </h2>
              <hr />
              <h2 className="text-success">サロン名の由来</h2>
              <h2 className="lead">
                「Gundari」という名前は、五大明王の一人にして南方の守護者「軍荼利」に由来します。モダンミステリースクール(MMS)での修行の過程でオーナーが知ることになった本当の自分の名前です。
              </h2>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4} md={4} sm={12} xs={12} className="mt-4 d-flex">
          <Card className="shadow-sm h-100 slideinBottom">
            <Card.Body className="text-center py-5 d-flex flex-column justify-content-center">
              <img
                src={require('../../images/salon_room.jpeg')}
                alt="画像は開発中のものです"
                className="img-fluid"
              />
            </Card.Body>
          </Card>
        </Col>
        <Col lg={12} md={12} sm={12} xs={12} className="mt-4">
          <Card className="shadow-sm h-100 slideinBottom">
            <Card.Body className="text-center py-5 d-flex flex-column justify-content-center">
              <Row className="justify-content-center align-items-center">
                <Col lg={6} md={6} sm={12} xs={12} className="mb-4">
                  <h1 className="display-4 mb-4 text-success">
                    オーナーからのメッセージ
                  </h1>
                </Col>
                <Col lg={6} md={6} sm={12} xs={12} className="mb-4">
                  <img
                    src={require('../../images/owner_face.jpeg')}
                    alt="画像は開発中のものです"
                    className="img-fluid"
                  />
                </Col>
              </Row>
              <h2 className="h3 text-muted mb-4">
                誰の中にも叶えたい願いはあります。皆がそれを忘れているだけ。
              </h2>
              <h2 className="h3 text-muted mb-4">
                日々の日常に埋もれ、本当の自分を見失うことで、誰しもがこの世界に対して悲観的になります。
              </h2>
              <h2 className="h3 text-muted mb-4">
                でも、そんな世界を変えることができるのは、あなた自身です。
              </h2>
              <h2 className="h3 text-muted mb-4">
                本当の自分を知ることで自分自身の見方が変わり、本当の自分の願いに生きることで、あなたの世界は変わります。
              </h2>
              <h2 className="h3 text-muted mb-4">
                目に見える世界が全てではない、魂・スピリットの世界はあるし、魔法だってある、誰の中にも神はいる。
              </h2>
              <h2 className="h3 text-muted mb-4">
                今の社会が決めた価値観に縛られない、あなた自身の価値観を大切にしてください。他人に合わせて自分を犠牲にする必要などありません。
              </h2>
              <h2 className="h3 text-muted mb-4">
                「他者を害さない」、「他者の選択の自由を奪わない」、これら善のルールを守っていれば誰もが自分を表現することができるのはこの世界に人間として肉体を持っている間の特権ですから。
              </h2>
              <h2 className="h3 text-muted mb-4">
                「楽しむことを許可していい」、かつて自分を見失っていた時にダイナビジョン株式会社のヒーラーの方々を通じて教えてもらった私のハイヤーセルフの言葉をそのまま送ります。生きてることを楽しもうよ、そのために生まれてくるんだから。
              </h2>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={12} md={12} sm={12} xs={12} className="mt-4">
          <Card className="shadow-sm h-100 slideinBottom">
            <Card.Body className="py-5">
              <h1 className="display-4 mb-4 text-success text-center">
                お知らせ・イベント情報
              </h1>

              {loading ? (
                <div className="text-center">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">読み込み中...</span>
                  </Spinner>
                </div>
              ) : articles.length > 0 ? (
                <div className="row">
                  {articles.map(article => (
                    <div key={article.id} className="col-md-6 col-lg-4 mb-3">
                      <Card
                        className="h-100 article-card"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleArticleClick(article)}
                      >
                        {article.featuredImage && (
                          <Card.Img
                            variant="top"
                            src={getImageUrl(article.featuredImage) || ''}
                            style={{ height: '150px', objectFit: 'cover' }}
                          />
                        )}
                        <Card.Body className="d-flex flex-column">
                          <Card.Title
                            className="h6 mb-2 text-truncate"
                            title={article.title}
                          >
                            {article.title}
                          </Card.Title>
                          {article.summary && (
                            <Card.Text
                              className="text-muted small mb-2"
                              style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {stripHtml(article.summary)}
                            </Card.Text>
                          )}
                          <div className="mt-auto">
                            <div className="d-flex flex-wrap gap-1 mb-2">
                              {article.categories
                                .slice(0, 2)
                                .map((category, index) => (
                                  <Badge
                                    key={index}
                                    bg="primary"
                                    className="small"
                                  >
                                    {category}
                                  </Badge>
                                ))}
                              {article.categories.length > 2 && (
                                <Badge bg="secondary" className="small">
                                  +{article.categories.length - 2}
                                </Badge>
                              )}
                            </div>
                            <small className="text-muted">
                              {formatDate(article.createdAt)} |{' '}
                              {article.authorName}
                            </small>
                          </div>
                        </Card.Body>
                      </Card>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted">
                  <p>まだお知らせはありません。</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 記事詳細モーダル */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{selectedArticle?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedArticle && (
            <>
              {selectedArticle.featuredImage && (
                <img
                  src={getImageUrl(selectedArticle.featuredImage) || ''}
                  alt={selectedArticle.title}
                  className="img-fluid mb-3 rounded"
                  style={{
                    maxHeight: '300px',
                    width: '100%',
                    objectFit: 'cover',
                  }}
                />
              )}

              <div className="mb-3">
                <div className="d-flex flex-wrap gap-1 mb-2">
                  {selectedArticle.categories.map((category, index) => (
                    <Badge key={index} bg="primary">
                      {category}
                    </Badge>
                  ))}
                </div>
                <div className="d-flex flex-wrap gap-1 mb-3">
                  {selectedArticle.tags.map((tag, index) => (
                    <Badge key={index} bg="secondary">
                      #{tag}
                    </Badge>
                  ))}
                </div>
                <small className="text-muted">
                  投稿日: {formatDate(selectedArticle.createdAt)} | 作成者:{' '}
                  {selectedArticle.authorName}
                  {selectedArticle.viewCount && (
                    <> | 閲覧数: {selectedArticle.viewCount}</>
                  )}
                </small>
              </div>

              {selectedArticle.summary && (
                <div className="mb-3 p-3 bg-light rounded">
                  <h6>概要</h6>
                  <p className="mb-0">{stripHtml(selectedArticle.summary)}</p>
                </div>
              )}

              <div
                className="article-content"
                dangerouslySetInnerHTML={{
                  __html: stripHtml(selectedArticle.content),
                }}
                style={{
                  lineHeight: '1.6',
                  fontSize: '1rem',
                }}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            閉じる
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Welcome;
